import type { RoomOptionsContainer } from '@elara/client-core/containers/RoomOptionsContainer';
import type { RoomSearchWidgetData } from '@elara/domain/models/schemas/widget-schemas';

import { useState } from 'react';

import { formatRoomSize, getBedTypeIcon, roomShortlistMetadata } from '@elara/client-core/lib/room-utils';
import { cn, formatPrice, pluralize } from '@elara/client-core/lib/utils';

import { useApp, useTripsState, useUIState } from '#states/hooks.js';
import { Alert, AlertDescription, ShortlistButton } from '#ui';
import { Button } from '#ui/button.js';
import { Card } from '#ui/card.js';
import { CoverImage } from '#ui/cards/DetailCardLayout.js';
import { useMessageRef } from '#ui/hooks/index.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

import { ViewAllRoomResultsButton } from './ViewAllRoomResultsButton.js';
import { WidgetErrorState } from './WidgetErrorState.js';

export type RoomSearchWidgetProps = RoomSearchWidgetData;

/**
 * RoomSearchWidget displays room search results by loading each option from Firestore.
 * Requires a current trip; results are stored under that trip when the tool runs.
 * Only rooms with an id (stored in Firestore) are shown.
 */
export function RoomSearchWidget({ request, hotel, rooms, warnings }: RoomSearchWidgetProps) {
  const uiState = useUIState();
  const tripsState = useTripsState();
  const tripId = tripsState.current?.id;
  const messageRef = useMessageRef();
  const [showAllRooms, setShowAllRooms] = useState(false);

  if (!request || !hotel || !rooms) return <WidgetErrorState message="No room data available" />;

  const roomsWithIds = rooms.filter((r): r is typeof r & { id: string } => typeof r?.id === 'string');
  const INITIAL_ROOM_COUNT = 5;
  const visibleRooms = showAllRooms ? roomsWithIds : roomsWithIds.slice(0, INITIAL_ROOM_COUNT);
  const hasMoreRooms = roomsWithIds.length > INITIAL_ROOM_COUNT;

  if (roomsWithIds.length === 0) {
    return (
      <Card className={cn('w-full my-4')}>
        <div className="p-6 text-center text-muted-foreground">
          <MaterialIcon icon="hotel" size={IconSizes.lg} className="mx-auto mb-2" />
          <p>No room options available</p>
        </div>
      </Card>
    );
  }

  if (!tripId) {
    return undefined;
  }

  return (
    <div className={cn('@container w-full flex flex-col items-start gap-4')}>
      {visibleRooms.map((roomData) => (
        <RoomOptionCard
          key={roomData.id}
          tripId={tripId}
          roomId={roomData.id}
          hotelName={hotel.name}
          messageRef={messageRef}
        />
      ))}
      {hasMoreRooms && !showAllRooms && (
        <Button variant="link" size="sm" onClick={() => setShowAllRooms(true)}>
          View {roomsWithIds.length - INITIAL_ROOM_COUNT} more available{' '}
          {pluralize(roomsWithIds.length - INITIAL_ROOM_COUNT, 'room')}
        </Button>
      )}
      {/* Every room the search saved, in the hotel guide (ELARA-1092). */}
      <ViewAllRoomResultsButton roomOptionId={roomsWithIds[0]!.id} />
    </div>
  );
}

function RoomOptionCard({
  tripId,
  roomId,
  messageRef,
}: {
  tripId: string;
  roomId: string;
  hotelName?: string;
  messageRef?: { chatId: string; messageId: string };
}) {
  const tripsState = useTripsState();
  const { features } = useApp('features');
  const roomOptions: RoomOptionsContainer = (features as { roomOptions: RoomOptionsContainer }).roomOptions;
  const handle = roomOptions.ClientRoomOption.get({ tripId, id: roomId });

  if (!handle.isReady || !handle.data) {
    return <div className="h-40 w-full rounded-md animate-pulse bg-muted/50" aria-busy="true" />;
  }

  if (handle.error) {
    return (
      <Alert variant="destructive" className="flex items-center gap-2">
        <MaterialIcon icon="error" />
        <AlertDescription>Error loading room</AlertDescription>
      </Alert>
    );
  }

  const roomOption = handle.data;
  // The rate the room leads with — the searched party's cheapest bookable open rate — not the cheapest
  // of any party, sold out or gated.
  const rate = roomOption.leadRate ?? roomOption.cheapestRate;
  const coverImage = roomOption.images?.[0]?.url;
  const coverImageCaption = roomOption.images?.[0]?.caption;

  const title = roomOption.displayName;
  const onClick = () => tripsState.showGuide('room', roomId);
  const actions = (
    <ShortlistButton
      itemType="room"
      title={`${roomOption.type} at ${roomOption.hotel?.name ?? 'Hotel'}`}
      referenceId={roomId}
      messageRef={messageRef}
      // The room, not a rate, priced for the party it was searched for.
      metadata={roomShortlistMetadata(roomOption)}
      variant="icon"
    />
  );

  return (
    <Card
      className={cn(
        'w-full border cursor-pointer transition-all hover:shadow-md overflow-hidden bg-background rounded-md',
      )}
      onClick={onClick}
    >
      <div className="p-4 @md:p-0 grid grid-cols-1 grid-rows-[auto_auto_auto] @md:grid-cols-3 @md:grid-rows-[auto_auto] gap-x-4 gap-y-4 @lg:gap-x-6 @md:gap-y-0">
        <CoverImage
          imageUrls={coverImage ? [coverImage] : undefined}
          title={coverImageCaption}
          type="room"
          size="lg"
          className="col-span-1 col-start-1 row-start-2 row-span-1 @md:row-span-2 my-0 bg-secondary text-sm flex items-center justify-center py-4 rounded-sm @md:rounded-r-none text-center @md:aspect-auto"
        />

        <div className="@md:min-h-[120px] col-span-1 col-start-1 row-start-1 row-span-1 @md:p-4 @md:pl-0 @md:col-span-2 @md:col-start-2 @md:row-start-1 flex flex-col gap-1">
          {/* Header */}
          <div className="flex items-center justify-between gap-4">
            <h3 className="text-lg font-medium mb-0">{title}</h3>

            <div className="flex items-center -mr-2 -mt-2" onClick={(e) => e.stopPropagation()}>
              {actions}
            </div>
          </div>

          {/* Details */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
            {roomOption.bedTypes.map((bedType) => (
              <div className="flex items-center gap-1" key={bedType.sabreBedTypeCode ?? bedType.description}>
                <MaterialIcon icon={getBedTypeIcon(bedType.sabreBedTypeCode ?? bedType.code)} size={IconSizes.sm} />
                <span>
                  {bedType.count} {pluralize(bedType.count, bedType.description)}
                </span>
              </div>
            ))}

            {roomOption.maxOccupancy && (
              <div className="flex items-center gap-1">
                <MaterialIcon icon={roomOption.maxOccupancy > 1 ? 'group' : 'person'} size={IconSizes.sm} />
                <span>Sleeps {roomOption.maxOccupancy}</span>
              </div>
            )}

            {roomOption.size && (
              <div className="flex items-center gap-1">
                <MaterialIcon icon="square_foot" size={IconSizes.sm} />
                <span>{formatRoomSize(roomOption.size)}</span>
              </div>
            )}

            {roomOption.view && (
              <div className="flex items-center gap-1">
                <MaterialIcon icon="landscape" size={IconSizes.sm} />
                <span>{roomOption.view}</span>
              </div>
            )}
          </div>
        </div>

        {/* Rate */}
        {rate && (
          <div className="col-span-1 col-start-1 row-start-3 row-span-1 @md:pb-4 @md:pr-4 @md:col-span-2 @md:col-start-2 @md:row-start-2 flex flex-col @md:flex-row @md:items-baseline gap-1 justify-between">
            <div className="flex flex-col gap-0.5">
              <div className="flex items-baseline gap-1">
                <div className="font-medium @lg:text-lg">
                  {formatPrice(rate.localPricing.avgNightlyRate, rate.localPricing.currency)}
                </div>
                <div className="text-xs">/ night</div>
              </div>
            </div>

            <div className="text-xs text-muted-foreground">
              {formatPrice(rate.localPricing.totalPrice, rate.localPricing.currency)} for {roomOption.nightsCount}{' '}
              {pluralize(roomOption.nightsCount ?? 1, 'night')}, 1 room
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
